import { useEffect, useState, type CSSProperties, type RefObject } from 'react';
import type { Seat } from '../game/match';

interface Point {
  x: number;
  y: number;
}

/**
 * A curved, animated arrow from the dealer's seat to the Rang-caller's seat,
 * showing cards are being dealt between them. Measures the real seat elements
 * (`data-seat="N"`) inside `containerRef` rather than guessing grid geometry,
 * so it stays correct across seat layouts and screen sizes.
 */
export function DealArrow({
  containerRef,
  from,
  to,
  color,
}: {
  containerRef: RefObject<HTMLElement | null>;
  from: Seat;
  to: Seat;
  /** The receiving (Rang-calling) team's colour, so the arrow reads as "their colour, incoming". */
  color: string;
}) {
  const [box, setBox] = useState<{ width: number; height: number } | null>(null);
  const [points, setPoints] = useState<{ from: Point; to: Point } | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || from === to) {
      setPoints(null);
      return;
    }

    const measure = () => {
      const fromEl = container.querySelector<HTMLElement>(`[data-seat="${from}"]`);
      const toEl = container.querySelector<HTMLElement>(`[data-seat="${to}"]`);
      if (!fromEl || !toEl) return;
      const cRect = container.getBoundingClientRect();
      const fRect = fromEl.getBoundingClientRect();
      const tRect = toEl.getBoundingClientRect();
      setBox({ width: cRect.width, height: cRect.height });
      setPoints({
        from: { x: fRect.left + fRect.width / 2 - cRect.left, y: fRect.top + fRect.height / 2 - cRect.top },
        to: { x: tRect.left + tRect.width / 2 - cRect.left, y: tRect.top + tRect.height / 2 - cRect.top },
      });
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(container);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [containerRef, from, to]);

  if (!points || !box || from === to) return null;

  // A single quadratic curve bowed toward the table centre, so it arcs over the
  // felt rather than cutting straight through the middle of the layout.
  const mid: Point = { x: (points.from.x + points.to.x) / 2, y: (points.from.y + points.to.y) / 2 };
  const center: Point = { x: box.width / 2, y: box.height / 2 };
  const bow = 0.55; // how far the curve bulges toward the centre
  const ctrl: Point = { x: mid.x + (center.x - mid.x) * bow, y: mid.y + (center.y - mid.y) * bow };

  // Pull the endpoints in from the seat centres so the arrow starts/ends at the
  // avatar's edge, not buried under it.
  const shrink = (p: Point, towards: Point, px: number): Point => {
    const dx = towards.x - p.x;
    const dy = towards.y - p.y;
    const len = Math.hypot(dx, dy) || 1;
    return { x: p.x + (dx / len) * px, y: p.y + (dy / len) * px };
  };
  const start = shrink(points.from, ctrl, 34);
  const end = shrink(points.to, ctrl, 40);

  const pathId = `deal-arrow-${from}-${to}`;
  const d = `M ${start.x} ${start.y} Q ${ctrl.x} ${ctrl.y} ${end.x} ${end.y}`;

  return (
    <>
      <svg
        className="pointer-events-none absolute inset-0 z-[1] h-full w-full overflow-visible"
        viewBox={`0 0 ${box.width} ${box.height}`}
        aria-hidden
      >
        <defs>
          <marker id={`${pathId}-head`} viewBox="0 0 10 10" refX="7" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
            <path d="M0 0 L10 5 L0 10 Z" fill={color} />
          </marker>
        </defs>
        <path id={pathId} d={d} fill="none" />
        {/* Soft trail so the arrow reads even before the dashes start moving. */}
        <path d={d} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeOpacity="0.35" />
        {/* The moving dashed stroke — "cards flowing" along the curve. */}
        <path
          d={d}
          fill="none"
          stroke={color}
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeDasharray="1 7"
          markerEnd={`url(#${pathId}-head)`}
          className="animate-deal-flow"
        />
      </svg>
      {/* Small playing-card sprites, each travelling the same curve, staggered — real cards being dealt. */}
      <div className="pointer-events-none absolute inset-0 z-[1] overflow-visible" aria-hidden>
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="animate-deal-card absolute top-0 left-0 block h-[14px] w-[10px] rounded-[2px] border border-gold/70 bg-cream shadow-[0_1px_4px_rgb(0_0_0/0.5)]"
            style={{ offsetPath: `path("${d}")`, animationDelay: `${i * 0.38}s` } as CSSProperties}
          />
        ))}
      </div>
    </>
  );
}
